import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { ClipboardList, RefreshCw, Download, Check, Package, AlertCircle, Loader2, ArrowUpDown } from 'lucide-react';
import { getRestockQueue, restockItem, restockAll, closeCurrentCycle, getCurrentCycle, exportRestockNeeded } from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SORT_OPTIONS = [
  { value: 'remaining_desc', label: 'Remaining (High → Low)' },
  { value: 'sold_desc', label: 'Sold (High → Low)' },
  { value: 'name_asc', label: 'Name (A–Z)' },
];

const RESTOCK_SORT_KEY = 'stockops_restock_sort';

const RestockQueuePage = () => {
  const [queue, setQueue] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restocking, setRestocking] = useState(null);
  const [showCloseCycleDialog, setShowCloseCycleDialog] = useState(false);
  const [closingCycle, setClosingCycle] = useState(false);
  const [partialRestock, setPartialRestock] = useState({ productId: null, qty: 0 });
  const [sortOption, setSortOption] = useState(() => {
    return localStorage.getItem(RESTOCK_SORT_KEY) || 'remaining_desc';
  });

  const fetchData = useCallback(async () => {
    try {
      const [queueRes, cycleRes] = await Promise.all([
        getRestockQueue(),
        getCurrentCycle(),
      ]);
      setQueue(queueRes.data);
      setCycle(cycleRes.data);
    } catch (error) {
      toast.error('Failed to load restock queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Persist sort option
  useEffect(() => {
    localStorage.setItem(RESTOCK_SORT_KEY, sortOption);
  }, [sortOption]);

  // Sorted queue
  const sortedQueue = useMemo(() => {
    const sorted = [...queue];
    sorted.sort((a, b) => {
      switch (sortOption) {
        case 'remaining_desc':
          return b.remaining_to_restock - a.remaining_to_restock;
        case 'sold_desc':
          return b.qty_sold_since_last_restock - a.qty_sold_since_last_restock;
        case 'name_asc':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    return sorted;
  }, [queue, sortOption]);

  const handleRestockFull = async (productId, qty) => {
    setRestocking(productId);
    try {
      await restockItem(productId, qty);
      toast.success('Item restocked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restock');
    } finally {
      setRestocking(null);
    }
  };

  const handleRestockPartial = async () => {
    if (!partialRestock.productId || partialRestock.qty < 1) return;
    
    setRestocking(partialRestock.productId);
    try {
      await restockItem(partialRestock.productId, partialRestock.qty);
      toast.success('Partial restock logged');
      setPartialRestock({ productId: null, qty: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restock');
    } finally {
      setRestocking(null);
    }
  };

  const handleRestockAll = async () => {
    setRestocking('all');
    try {
      const res = await restockAll();
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to restock all items');
    } finally {
      setRestocking(null);
    }
  };

  const handleCloseCycle = async () => {
    setClosingCycle(true);
    try {
      await closeCurrentCycle();
      toast.success('Restock cycle closed. New cycle started.');
      setShowCloseCycleDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close cycle');
    } finally {
      setClosingCycle(false);
    }
  };

  const handleExport = async () => {
    try {
      await exportRestockNeeded();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-enter" data-testid="restock-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-bold">Restock Queue</h1>
          <p className="text-sm text-muted-foreground">
            {queue.length} item{queue.length !== 1 ? 's' : ''} need restocking
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="btn-icon"
            aria-label="Export"
            data-testid="export-restock-btn"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={fetchData}
            className="btn-icon"
            aria-label="Refresh"
            data-testid="refresh-queue-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      {queue.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={handleRestockAll}
            disabled={restocking === 'all'}
            className="h-12 font-semibold rounded-xl bg-success text-white hover:bg-success/90"
            data-testid="restock-all-btn"
          >
            {restocking === 'all' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Restock All
              </>
            )}
          </Button>
          <Button
            onClick={() => setShowCloseCycleDialog(true)}
            variant="outline"
            className="h-12 font-semibold rounded-xl"
            data-testid="close-cycle-btn"
          >
            <ClipboardList className="w-5 h-5 mr-2" />
            Close Cycle
          </Button>
        </div>
      )}

      {/* Sort Control */}
      {queue.length > 1 && (
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
          <Select value={sortOption} onValueChange={setSortOption}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="restock-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Queue List */}
      {queue.length === 0 ? (
        <div className="empty-state py-12">
          <Package className="empty-state-icon" />
          <h3 className="empty-state-title">All Caught Up!</h3>
          <p className="empty-state-text">No items need restocking right now</p>
        </div>
      ) : (
        <div 
          className="space-y-3" 
          data-testid="restock-queue-list"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {sortedQueue.map((item) => (
            <div 
              key={item.product_id} 
              className="item-card"
              data-testid={`restock-item-${item.sku}`}
            >
              <div className="flex items-start gap-3">
                {item.image_url ? (
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-14 h-14 rounded-lg object-cover bg-slate-100"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Package className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{item.name}</h3>
                  <p className="text-xs text-muted-foreground font-data">{item.sku}</p>
                  
                  <div className="flex items-center gap-4 mt-2">
                    <div className="text-center">
                      <span className="block text-lg font-data font-bold text-destructive">
                        {item.qty_sold_since_last_restock}
                      </span>
                      <span className="text-xs text-muted-foreground">Sold</span>
                    </div>
                    <div className="text-center">
                      <span className="block text-lg font-data font-bold text-success">
                        {item.qty_restocked_since_last_restock}
                      </span>
                      <span className="text-xs text-muted-foreground">Restocked</span>
                    </div>
                    <div className="text-center">
                      <span className="block text-lg font-data font-bold text-warning">
                        {item.remaining_to_restock}
                      </span>
                      <span className="text-xs text-muted-foreground">Remaining</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Restock Actions */}
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => setPartialRestock({ productId: item.product_id, qty: 1 })}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid={`partial-restock-${item.sku}`}
                >
                  Partial
                </Button>
                <Button
                  onClick={() => handleRestockFull(item.product_id, item.remaining_to_restock)}
                  disabled={restocking === item.product_id}
                  size="sm"
                  className="flex-1 bg-success hover:bg-success/90 text-white"
                  data-testid={`full-restock-${item.sku}`}
                >
                  {restocking === item.product_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Full ({item.remaining_to_restock})
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Partial Restock Dialog */}
      <Dialog 
        open={!!partialRestock.productId} 
        onOpenChange={() => setPartialRestock({ productId: null, qty: 0 })}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Partial Restock</DialogTitle>
            <DialogDescription>
              Enter the quantity you're restocking
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="number"
              min="1"
              value={partialRestock.qty}
              onChange={(e) => setPartialRestock({ ...partialRestock, qty: parseInt(e.target.value) || 0 })}
              className="input-large text-center font-data text-2xl"
              autoFocus
              data-testid="partial-qty-input"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={handleRestockPartial}
              disabled={partialRestock.qty < 1 || restocking === partialRestock.productId}
              className="w-full btn-primary-action"
              data-testid="confirm-partial-restock"
            >
              {restocking === partialRestock.productId ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  <span>Confirm Restock</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Cycle Dialog */}
      <Dialog open={showCloseCycleDialog} onOpenChange={setShowCloseCycleDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-warning" />
              Close Restock Cycle?
            </DialogTitle>
            <DialogDescription>
              This will mark all current items as handled and start a new cycle. 
              Historical data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCloseCycleDialog(false)}
              className="flex-1"
              data-testid="cancel-close-cycle"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloseCycle}
              disabled={closingCycle}
              className="flex-1 bg-warning hover:bg-warning/90 text-white"
              data-testid="confirm-close-cycle"
            >
              {closingCycle ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Close Cycle'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestockQueuePage;
